import React, { useState, useEffect } from 'react';
import { Navigate } from "react-router-dom";
import {
  ArrowBack,
} from "@mui/icons-material";
import { Fab, Tooltip } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  DisablableOption,
  ModelBase,
  SmartHash
} from "@models";
import { 
  AttachedTable,
  Creature,
  CreatureInstance,
  LegendariesTable,
  SpellcastingTable,
  TableEntry, 
} from "@dndModels";
import { 
  CREATURE_TYPES, 
  CR_OPTIONS,
  SIZE_TO_NUM_MAP,
} from "@dndModels/Constants";
import { CreatureType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

import ModelBaseDetails from "@dndComponents/ModelBaseDetails";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import LoadingBox from "@components/input/LoadingBox";
import DeleteButton from "@components/input/buttons/DeleteButton";
import FilterBox from "@dndComponents/filtering/FilterBox";
import DisplayObject from "@dndComponents/display/DisplayObject";
import ToastBox from "@components/display/ToastBox";
import CheckBox from "@components/input/CheckBox";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CreatureMergeAdd() {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState("");
  const [reloading, setReloading] = useState("");
  const [obj, setObj] = useState<Creature>(api.applyWorkingOn(new Creature()));
  const [creatures, setCreatures] = useState<Creature[]>([]);
  const [options, setOptions] = useState<DisablableOption[]>([]);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [filter, setFilter] = useState({});
  const [merging_creatures, setMergingCreatures] = useState<{ creature: Creature, min_cr: number }[]>([]);
  const [change_time, setChangeTime] = useState(-1);
  const [toast_time, setToastTime] = useState(-1);
  const [toast_message, setToastMessage] = useState("");
  const [default_name, setDefaultName] = useState(true);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  });

  useEffect(() => {
    if(reloading !== "") {
      setReloading("");
    }
  }, [reloading, setReloading]);

  const load = () => {
    setLoading(true);
    const onComplete = (res: SmartHash) => {
      setCreatures(res.creature as Creature[]);
      setLoading(false);
    };
    const onProgress = (data_type: string, percentage: number ) => {
      if (percentage !== 0) {
        setLoadingMessage(`Loading Creatures ${Math.floor(percentage * 100)}%`);
      } else {
        setLoadingMessage(`Loading Creatures`);
      }
    };
    const onError = (message: string) => {
      setLoadingMessage(message);
    };
    // debugger;
    api.getSetOfObjectsWithCallbacks({ 
      data_types: ["creature"],
      onProgress, onComplete, onError
    });
    setFetched(true);
  }

  const submit = async () => {
    if (api.user && api.user.admin && merging_creatures.length > 0) {
      setProcessing(true);
      debugger;
      const use_merging_creatures = merging_creatures.sort((a,b) => { return a.min_cr - b.min_cr; });
      obj.min_challenge_rating = Math.min(...merging_creatures.map(o => o.min_cr));
      obj.max_challenge_rating = Math.max(...merging_creatures.map(o => {
        if (o.creature) {
          return o.creature.max_challenge_rating;
        }
        return -1;
      }));
      obj.challenge_rating = obj.min_challenge_rating;
      obj.age_category = "";
      obj.table = new AttachedTable();
      obj.name_table = [];
      obj.spellcasting_table = new SpellcastingTable();
      obj.resources_table = new AttachedTable();
      obj.legendaries_table = new LegendariesTable();
      obj.fbs = [];
      for (let i = 0; i < use_merging_creatures.length; ++i) {
        const merging_creature = use_merging_creatures[i];
        const creature = merging_creature.creature;
        const next_cr = use_merging_creatures.length > i + 1 ? use_merging_creatures[i + 1].min_cr : creature.max_challenge_rating + 1;
        const min_cr = merging_creature.min_cr;
        // Add to names
        obj.name_table.push({ level: min_cr, name: creature.name, age_category: creature.age_category });
        // Add to tables
        for (let cr = min_cr; cr < next_cr; ++cr) {
          const ci = new CreatureInstance();
          await ci.copyCreature(creature, cr);
          // Main Table
          const old_entry = creature.table.entries.find(o => o.level === cr);
          if (old_entry) {
            const entry = old_entry.clone();
            const keys = Object.keys(entry.data);
            if (!keys.includes("Hit Dice Count")) {
              let count = 0;
              if (creature.hit_dice.length > 0) {
                count = creature.hit_dice[0].count.base;
                if (!creature.hit_dice[0].count.isBasic()) {
                  // if it's not basic it's usually just a CR multiplier,
                  // and we don't want to worry about anything else.
                  count += creature.hit_dice[0].count.add_char_level_mult * entry.level;
                }
              }
              entry.data["Hit Dice Count"] = Math.floor(count);
            }
            if (!keys.includes("Armor Class")) {
              if (creature.armor_class.add_char_level_mult > 0) {
                entry.data["Armor Class"] = creature.armor_class.base + creature.armor_class.add_char_level_mult * entry.level;
              } else {
                entry.data["Armor Class"] = creature.armor_class.base;
              }
            }
            if (!keys.includes("Size")) {
              entry.data["Size"] = creature.size_ugn.base;
            }
            obj.table.entries.push(entry);
          } else if (cr === min_cr) {
            const entry = new TableEntry();
            entry.level = cr;
            entry.data["STR"] = ci.current_ability_scores.strength;
            entry.data["DEX"] = ci.current_ability_scores.dexterity;
            entry.data["CON"] = ci.current_ability_scores.constitution;
            entry.data["INT"] = ci.current_ability_scores.intelligence;
            entry.data["WIS"] = ci.current_ability_scores.wisdom;
            entry.data["CHA"] = ci.current_ability_scores.charisma;
            entry.data["Hit Dice Count"] = Math.floor(ci.hit_dice[0].count);
            entry.data["Armor Class"] = creature.armor_class.base; // Use creature for this one so that if it includes DEX, that won't be effected
            entry.data["Size"] = SIZE_TO_NUM_MAP[ci.size];
            obj.table.entries.push(entry);
          }
          // x_day 
          const x_day_entry = creature.spellcasting_table.entries.find(o => o.level === cr);
          if (x_day_entry) {
            const old_entry = obj.spellcasting_table.getEntry(cr);
            if (!old_entry || old_entry.description !== x_day_entry.description || 
              !common_util.objectsMatch(old_entry.spells, x_day_entry.spells)) {
              obj.spellcasting_table.entries.push(x_day_entry.clone());
            }
          }
          // resources
          const resources_entry = creature.resources_table.entries.find(o => o.level === cr);
          if (resources_entry) {
            console.log(resources_entry);
            const old_entry = obj.resources_table.getEntry(cr);
            if (!old_entry || !common_util.objectsMatch(old_entry.data, resources_entry.data)) {
              obj.resources_table.entries.push(resources_entry.clone());
            }
          }
          // legendaries
          const leg_entry = creature.legendaries_table.entries.find(o => o.level === cr);
          if (leg_entry) {
            const old_entry = obj.legendaries_table.getEntry(cr);
            if (!old_entry || !common_util.objectsMatch(old_entry, leg_entry)) {
              obj.legendaries_table.entries.push(leg_entry.clone());
            }
          }
          // attributes
          const attr_entry = creature.attributes_table.entries.find(o => o.level === cr);
          if (attr_entry) {
            const old_entry = obj.attributes_table.getEntry(cr);
            if (!old_entry) {
              obj.attributes_table.entries.push(attr_entry.clone());
            } else {
              const clone = old_entry.clone();
              clone.level = attr_entry.level;
              if (!common_util.objectsMatch(old_entry, attr_entry)) {
                obj.attributes_table.entries.push(attr_entry.clone());
              }
            }
          }
        }
        // Add FBs, replacing previous FBs of same type and name
        for (const fb of creature.fbs) {
          const new_fb = fb.clone();
          if (new_fb.level < merging_creature.min_cr) {
            new_fb.level = merging_creature.min_cr;
          }
          const old_fbs = obj.fbs.filter(o => o.name === fb.name);
          if (old_fbs.length > 0) {
            const max_level = Math.max(...old_fbs.map(o => o.level));
            const old_fb = old_fbs.find(o => o.level === max_level);
            if (old_fb && new_fb.features[0].floating_feature && 
              old_fb.features[0].floating_feature && 
              new_fb.features[0].floating_feature.description !== old_fb.features[0].floating_feature.description) {
              new_fb.upgrades_feature_base_id = old_fb.true_id;
              new_fb.upgrades_feature_base = old_fb;
              obj.fbs.push(new_fb);
            }
          } else {
            obj.fbs.push(new_fb);
          }
        }
        // for (const fb of creature.legendary_action_fbs) {
        //   const new_fb = fb.clone();
        //   if (new_fb.level < merging_creature.min_cr) {
        //     new_fb.level = merging_creature.min_cr;
        //   }
        //   const old_fbs = obj.legendary_action_fbs.filter(o => o.name === fb.name);
        //   if (old_fbs.length > 0) {
        //     const max_level = Math.max(...old_fbs.map(o => o.level));
        //     const old_fb = old_fbs.find(o => o.level === max_level);
        //     if (old_fb && new_fb.features[0].floating_feature && 
        //       old_fb.features[0].floating_feature && 
        //       new_fb.features[0].floating_feature.description !== old_fb.features[0].floating_feature.description) {
        //       new_fb.upgrades_feature_base_id = old_fb.true_id;
        //       new_fb.upgrades_feature_base = old_fb;
        //       obj.fbs.push(new_fb);
        //     }
        //   } else {
        //     obj.fbs.push(new_fb);
        //   }
        // }
        // for (const fb of creature.lair_action_fbs) {
        //   const new_fb = fb.clone();
        //   if (new_fb.level < merging_creature.min_cr) {
        //     new_fb.level = merging_creature.min_cr;
        //   }
        //   const old_fbs = obj.lair_action_fbs.filter(o => o.name === fb.name);
        //   if (old_fbs.length > 0) {
        //     const max_level = Math.max(...old_fbs.map(o => o.level));
        //     const old_fb = old_fbs.find(o => o.level === max_level);
        //     if (old_fb && new_fb.features[0].floating_feature && 
        //       old_fb.features[0].floating_feature && 
        //       new_fb.features[0].floating_feature.description !== old_fb.features[0].floating_feature.description) {
        //       new_fb.upgrades_feature_base_id = old_fb.true_id;
        //       new_fb.upgrades_feature_base = old_fb;
        //       obj.fbs.push(new_fb);
        //     }
        //   } else {
        //     obj.fbs.push(new_fb);
        //   }
        // }
        // for (const fb of creature.traits) {
        //   const new_fb = fb.clone();
        //   if (new_fb.level < merging_creature.min_cr) {
        //     new_fb.level = merging_creature.min_cr;
        //   }
        //   const old_fbs = obj.traits.filter(o => o.name === fb.name);
        //   if (old_fbs.length > 0) {
        //     const max_level = Math.max(...old_fbs.map(o => o.level));
        //     const old_fb = old_fbs.find(o => o.level === max_level);
        //     if (old_fb && new_fb.features[0].floating_feature && 
        //       old_fb.features[0].floating_feature && 
        //       new_fb.features[0].floating_feature.description !== old_fb.features[0].floating_feature.description) {
        //       new_fb.upgrades_feature_base_id = old_fb.true_id;
        //       new_fb.upgrades_feature_base = old_fb;
        //       obj.fbs.push(new_fb);
        //     }
        //   } else {
        //     obj.fbs.push(new_fb);
        //   }
        // }
      }
      // Make sure Stats, AC, Size, etc are all set to use tables
      obj.ability_scores.strength.base = 0;
      obj.ability_scores.strength.makeBasic();
      obj.ability_scores.strength.table_type = "Attached";
      obj.ability_scores.strength.table_id = "Attached";
      obj.ability_scores.strength.table_field = "STR";
      obj.ability_scores.dexterity.base = 0;
      obj.ability_scores.dexterity.makeBasic();
      obj.ability_scores.dexterity.table_type = "Attached";
      obj.ability_scores.dexterity.table_id = "Attached";
      obj.ability_scores.dexterity.table_field = "DEX";
      obj.ability_scores.constitution.base = 0;
      obj.ability_scores.constitution.makeBasic();
      obj.ability_scores.constitution.table_type = "Attached";
      obj.ability_scores.constitution.table_id = "Attached";
      obj.ability_scores.constitution.table_field = "CON";
      obj.ability_scores.intelligence.base = 0;
      obj.ability_scores.intelligence.makeBasic();
      obj.ability_scores.intelligence.table_type = "Attached";
      obj.ability_scores.intelligence.table_id = "Attached";
      obj.ability_scores.intelligence.table_field = "INT";
      obj.ability_scores.wisdom.base = 0;
      obj.ability_scores.wisdom.makeBasic();
      obj.ability_scores.wisdom.table_type = "Attached";
      obj.ability_scores.wisdom.table_id = "Attached";
      obj.ability_scores.wisdom.table_field = "WIS";
      obj.ability_scores.charisma.base = 0;
      obj.ability_scores.charisma.makeBasic();
      obj.ability_scores.charisma.table_type = "Attached";
      obj.ability_scores.charisma.table_id = "Attached";
      obj.ability_scores.charisma.table_field = "CHA";
      obj.hit_dice[0].count.base = 0;
      obj.hit_dice[0].count.table_type = "Attached";
      obj.hit_dice[0].count.table_id = "Attached";
      obj.hit_dice[0].count.table_field = "Hit Dice Count";
      // hit_dice size is based on creature size
      obj.armor_class.base = 0;
      obj.armor_class.table_type = "Attached";
      obj.armor_class.table_id = "Attached";
      obj.armor_class.table_field = "Armor Class";
      obj.size_ugn.base = 0;
      obj.size_ugn.table_type = "Attached";
      obj.size_ugn.table_id = "Attached";
      obj.size_ugn.table_field = "Size";
      obj.table.field_order = {
        0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
      };
      obj.table.extrapolateEmptyFields();

      const resource_fields = obj.resources_table.entries.flatMap(o => { return Object.keys(o.data); });
      for (const entry of obj.resources_table.entries) { 
        for (const field of resource_fields) {
          if (!Object.keys(entry.data).includes(field)) {
            entry.data[field] = 0;
          }
        }
      }

      api.upsertObject("creature", obj).then(res => {
        setProcessing(false);
        setNavigateTo("/dandd/creature");
      });
    } else {
      setNavigateTo("/dandd/creature");
    }
  }

  const updateFilter = (changed: any) => {
    setFilter(changed);
    const filtered = api.filter("creature", creatures, changed) as Creature[];
    
    const new_options: DisablableOption[] = filtered.filter(o => o.min_challenge_rating >= 1).map(o => {
      const opt: DisablableOption = {
        id: o._id,
        text: `${o.name} (${o.min_challenge_rating} - ${o.max_challenge_rating})`
      };
      return opt;
    });
    setOptions(new_options);
    setReloading("creatures");
  }

  const renderCreaturesToMerge = () => {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <FilterBox
            data_type="creature"
            the_filter={filter}
            all_objects={creatures}
            onChange={updateFilter}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { reloading !== "creatures" && 
            <SelectStringBox
              name="Merging Creatures"
              options={options}
              values={merging_creatures.map(o => o.creature._id)}
              onChange={(changed: string[]) => {
                const new_obj = obj;
                const new_merging_creatures = merging_creatures.filter(o => changed.includes(o.creature._id));
                const old_ids = new_merging_creatures.map(o => o.creature._id);
                const missing = changed.filter(o => !old_ids.includes(o));
                for (const id of missing) {
                  const creature = creatures.find(o => o._id === id);
                  if (creature) {
                    let min_cr = creature.min_challenge_rating;
                    let possible_crs = CR_OPTIONS.map(o => +o).filter(o => o >= min_cr && o <= creature.max_challenge_rating);
                     
                    while (new_merging_creatures.filter(o => o.min_cr === min_cr).length > 0) {
                      possible_crs = possible_crs.slice(1);
                      if (possible_crs.length === 0) {
                        setToastMessage(`Unable to add ${creature.name}`);
                        setToastTime(common_util.get_time_number());
                        min_cr = -1;
                        break;
                      }
                      min_cr = possible_crs[0];
                    }
                    if (min_cr > -1) {
                      // Make sure we're not trying to make it use it beyond where it's designed
                      new_merging_creatures.push({ creature, min_cr });
                    }
                  }
                }
                if (new_merging_creatures.length > 0) {
                  let name = new_obj.name;
                  // Copy stuff from the lowest CR one into obj
                  const min_cr = Math.min(...new_merging_creatures.map(o => o.min_cr));
                  const lowest = new_merging_creatures.find(o => o.min_cr === min_cr);
                  if (lowest) {
                    new_obj.copy(lowest.creature, true);
                    if (default_name) {
                      let name_pieces = lowest.creature.name.split(" ");
                      for (const merging_creature of new_merging_creatures) {
                        const new_name_pieces = merging_creature.creature.name.split(" ");
                        name_pieces = name_pieces.filter(o => new_name_pieces.includes(o));
                      }
                      name = name_pieces.join(" ");
                    }
                  }
                  new_obj.name = name;
                }
                setObj(new_obj);
                setMergingCreatures(new_merging_creatures);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
          <ToastBox
            openTime={ toast_time }
          >
            {toast_message}
          </ToastBox>
        </Grid>
        { reloading !== "creatures" && merging_creatures.sort((a, b) => { return a.min_cr - b.min_cr; }).map((merging_creature, key) => {
          const creature = merging_creature.creature;
          // if (creature.name.includes("Adult Vine")) {
          //   debugger;
          // }
          const max_under_min = Math.max(...merging_creatures.filter(o => o.min_cr < merging_creature.min_cr).map(o => o.min_cr), -1);
          const min_over_min = Math.min(...merging_creatures.filter(o => o.min_cr > merging_creature.min_cr).map(o => o.min_cr),1000);
          const min = creature.min_challenge_rating;
          const max = creature.max_challenge_rating;
          let possible_crs = CR_OPTIONS.map(o => +o).filter(o => o >= min && o <= max);
          if (max_under_min >= min) {
            possible_crs = possible_crs.filter(o => o > max_under_min);
          }
          if (min_over_min <= max) {
            possible_crs = possible_crs.filter(o => o < min_over_min);
          }
          if (possible_crs.length === 0) {
            console.error("No possible CRs. Should never come here");
          }
          const cr_options = possible_crs.map(o => { return `${o}`; });
          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 4
                }}>
                <DisplayObject obj={merging_creature.creature} />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <SelectStringBox
                  name="Starting CR"
                  value={`${merging_creature.min_cr}`}
                  options={cr_options}
                  onChange={(changed: string) => {
                    const new_merging_creatures = merging_creatures;
                    const this_creature = new_merging_creatures.find(o => o.creature._id === merging_creature.creature._id);
                    if (this_creature) {
                      this_creature.min_cr = +changed;
                    }
                    setMergingCreatures(new_merging_creatures);
                    setReloading("creatures");
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DeleteButton
                  onClick={() => {
                    const new_merging_creatures = merging_creatures.filter(o => o.creature._id !== merging_creature.creature._id);
                    setMergingCreatures(new_merging_creatures);
                    setReloading("creatures");
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  }

  if (!fetched || loading) {
    const height = window.innerHeight - 64;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} message={loading_message} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <Tooltip title={`Back to Creatures`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                setNavigateTo(`/dandd/creature`);
              }}>
              <ArrowBack/>
            </Fab>
          </Tooltip>&nbsp;
          <span className="header">
            Merging Creatures into new Creature
          </span>
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <StringBox 
            name="Name"
            value={obj.name} 
            message={obj.name.length > 0 ? "" : "Name Invalid"} 
            disabled={default_name}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.name = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox 
            name="Use Default"
            value={default_name} 
            onChange={setDefaultName}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectStringBox 
            name="Creature Type" 
            options={ CREATURE_TYPES }
            value={obj.creature_type} 
            onChange={(changed: CreatureType) => {
              const new_obj = obj;
              new_obj.creature_type = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }} 
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <SuggestionStringBox 
            name="Subtype"
            field="subtypes"
            values={obj.subtypes}
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.subtypes = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { renderCreaturesToMerge() }
        <Grid
          size={{
            xs: 12
          }}>
          <ModelBaseDetails
            obj={obj}
            no_grid
            change_time={change_time}
            onSubmit={submit}
            processing={processing}
            onChange={(changed: ModelBase) => {
              if (changed instanceof Creature) {
                const new_obj = obj;
                new_obj.copy(changed);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }
            }}
          />
        </Grid>
      </Grid>
    );
  }
}
